export const ROLES = ['owner', 'guest'] as const

export type TeleconsultationRole = (typeof ROLES)[number]

export type ParticipantView = 'waiting' | 'room'

export type LeavePrompt = 'owner_choice' | 'owner_leave' | 'guest_waiting' | 'guest_room'

export function leavePrompt(
  role: TeleconsultationRole,
  view: ParticipantView,
  canEnd: boolean,
): LeavePrompt {
  if (role === 'owner') return canEnd ? 'owner_choice' : 'owner_leave'
  return view === 'waiting' ? 'guest_waiting' : 'guest_room'
}

export interface LocalPermissionsLike {
  canSubscribe: boolean
}

export function deriveParticipantView(
  permissions: LocalPermissionsLike | undefined | null,
): ParticipantView {
  return permissions?.canSubscribe ? 'room' : 'waiting'
}

export interface RemotePermissionsLike {
  canPublish: boolean
}

export function isWaitingForAdmission(
  permissions: RemotePermissionsLike | undefined | null,
): boolean {
  return !(permissions?.canPublish ?? false)
}

export const MAX_RECONNECT_ATTEMPTS = 5

export const RECONNECT_BACKOFF_CEILING_MS = 8_000

export function reconnectBackoffMs(attempt: number): number {
  return Math.min(1000 * 2 ** Math.max(attempt - 1, 0), RECONNECT_BACKOFF_CEILING_MS)
}

export function buildParticipantUrl(template: string | null, participantId: number): string | null {
  return template?.replace('__PARTICIPANT_ID__', String(participantId)) ?? null
}

export type DisconnectClassification = 'left' | 'removed' | 'ended_by_owner' | 'reconnect'

const REMOVED_REASONS = new Set(['PARTICIPANT_REMOVED', 'DUPLICATE_IDENTITY'])
const ENDED_BY_OWNER_REASONS = new Set(['ROOM_DELETED', 'ROOM_CLOSED', 'SERVER_SHUTDOWN'])

export function classifyDisconnect(reasonName: string): DisconnectClassification {
  if (reasonName === 'CLIENT_INITIATED') return 'left'
  if (REMOVED_REASONS.has(reasonName)) return 'removed'
  if (ENDED_BY_OWNER_REASONS.has(reasonName)) return 'ended_by_owner'

  return 'reconnect'
}

export interface ParticipantNameLike {
  identity: string
  name?: string
}

export function participantDisplayName(participant: ParticipantNameLike): string {
  return participant.name || participant.identity
}

export const CONNECTION_QUALITIES = ['excellent', 'good', 'poor', 'lost'] as const

export type ConnectionQualityName = (typeof CONNECTION_QUALITIES)[number]

export interface ConnectionQualitySubject {
  quality: ConnectionQualityName
  local: boolean
  name: string
}

function isConnectionQuality(value: string): value is ConnectionQualityName {
  return (CONNECTION_QUALITIES as readonly string[]).includes(value)
}

export function describeConnectionQuality(
  quality: string,
  participant: (ParticipantNameLike & { isLocal: boolean }) | undefined,
): ConnectionQualitySubject | null {
  if (!participant || !isConnectionQuality(quality)) return null

  return { quality, local: participant.isLocal, name: participantDisplayName(participant) }
}

export const RECONNECTING_NOTICE_DELAY_MS = 2_000

export interface TileStreamState {
  streamState: string
  isEnabled: boolean
  isMuted: boolean
  isLocal: boolean
}

// `paused` is the server's downlink allocator; a track the client itself disabled never earns it.
export function pausedOverlay({
  streamState,
  isEnabled,
  isMuted,
  isLocal,
}: TileStreamState): boolean {
  return streamState === 'paused' && isEnabled && !isMuted && !isLocal
}
